import { memo, useRef, useEffect, useState, useCallback } from 'react';
import { Shuffle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FinalizedSegment } from '@/hooks/useDeepgramTranscriber';
import { JOURNAL_PROMPTS } from './prompts';

interface ImmersiveTranscriptionProps {
  segments: FinalizedSegment[];
  interimText: string;
  streamingText: string;
}

/** Single finalized segment — latest is bright, older ones dim */
const FinalizedSpan = memo<{ segment: FinalizedSegment; isLatest: boolean }>(
  ({ segment, isLatest }) => {
    return (
      <span
        className={cn(
          'transition-colors duration-500',
          isLatest ? 'text-white font-semibold' : 'text-white/40'
        )}
      >
        {segment.text}
      </span>
    );
  }
);
FinalizedSpan.displayName = 'FinalizedSpan';

/** Empty state prompt — shuffleable topic suggestion */
function PromptButton() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * JOURNAL_PROMPTS.length));

  const shuffle = useCallback(() => {
    setIndex(prev => {
      let next: number;
      do { next = Math.floor(Math.random() * JOURNAL_PROMPTS.length); } while (next === prev && JOURNAL_PROMPTS.length > 1);
      return next;
    });
  }, []);

  return (
    <button
      onClick={shuffle}
      className="flex flex-col items-center gap-3 text-white/50 hover:text-white/70 transition-colors"
    >
      <span className="text-2xl">✨</span>
      <span className="text-base sm:text-lg font-medium">{JOURNAL_PROMPTS[index]}</span>
      <span className="flex items-center gap-1.5 text-xs text-white/30">
        <Shuffle className="h-3 w-3" />
        タップでお題を変更
      </span>
    </button>
  );
}

const ImmersiveTranscription = memo<ImmersiveTranscriptionProps>(
  ({ segments, interimText, streamingText }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom on new content
    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, [segments, interimText, streamingText]);

    const isEmpty = segments.length === 0 && !interimText && !streamingText;

    return (
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-y-auto px-6 py-4 min-h-0',
          isEmpty && 'flex items-center justify-center'
        )}
      >
        {isEmpty ? (
          <PromptButton />
        ) : (
          <div className="text-lg sm:text-xl leading-relaxed text-center">
            {/* Finalized segments */}
            {segments.map((seg, i) => (
              <FinalizedSpan
                key={`${i}-${seg.finalizedAt}`}
                segment={seg}
                isLatest={i === segments.length - 1}
              />
            ))}

            {/* Interim text (currently being spoken) */}
            {interimText && (
              <span className="text-primary/80 font-medium">
                {interimText}
              </span>
            )}

            {/* System message (e.g., waiting for final results) */}
            {streamingText && (
              <span className="text-white/30 bg-white/5 rounded px-1 ml-1 text-sm">
                {streamingText}
              </span>
            )}

            {/* Animated cursor */}
            {(segments.length > 0 || interimText) && (
              <span className="inline-block w-0.5 h-5 bg-primary animate-pulse ml-0.5 align-middle" />
            )}
          </div>
        )}
      </div>
    );
  }
);
ImmersiveTranscription.displayName = 'ImmersiveTranscription';

export default ImmersiveTranscription;
